import { Check, X } from "lucide-react";
import { SUBSCRIPTION_PLANS } from "../const/subscriptionPlan";
import { CREDIT_TOPUP_PACKS } from "../const/creditTopUpPacks";
import { useAuth } from "../contexts/AuthContext";
import { usePayment } from "../hooks/usePayment";
import { useSubscription } from "../hooks/useSubscription";

import UpgradeModal from "../components/Modal/UpgradeModal";
import { useState } from "react";
import { toast } from "react-hot-toast";

const Plans = () => {
	const { user } = useAuth();
	const { hasActiveSubscription } = useSubscription();

	const { initiatePayment, error: paymentError } = usePayment();
	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

	const handlePurchase = async (planType) => {
		if (!user) {
			toast.error("User not found");
			return;
		}

		try {
			await initiatePayment({ user_id: user.id || user._id, planType });
		} catch (error) {
			toast.error(paymentError || "Payment initiation failed");
		}
	};

	const FeatureRow = ({ name, free, monthly }) => (
		<div className="grid grid-cols-[2fr_1fr_1fr] p-4 border-b border-gray-100 items-center last:border-b-0 text-sm md:text-base min-w-[500px]">
			<div className="font-medium text-gray-700">{name}</div>
			<div className="text-center text-gray-700 flex justify-center">{free}</div>
			<div className="text-center text-indigo-600 font-semibold bg-indigo-50 rounded px-2 py-1 flex justify-center">{monthly}</div>
		</div>
	);

	if (!user) return null;

	return (
		<div
			className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
			style={{
				backgroundImage: "url('/BGForSections.webp')",
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<div className="max-w-7xl mx-auto">
				<header className="text-center mb-16">
					<h1 className="text-4xl font-extrabold text-indigo-600 mb-4 sm:text-5xl">
						Choose Your Cosmic Journey
					</h1>
					<p className="text-xl text-gray-500 max-w-2xl mx-auto">
						Unlock the wisdom of the stars with a plan that resonates with your soul.
					</p>
				</header>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
					<div
						className="rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col hover:shadow-lg transition-shadow duration-300 relative overflow-hidden text-black h-full"
						style={{
							background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.94))",
						}}
					>
						<div className="text-center mb-8">
							<h3 className="text-2xl font-semibold text-gray-900 mb-2">Free Tier</h3>
							<div className="flex justify-center items-baseline mb-2">
								<span className="text-5xl font-bold text-gray-900">$0</span>
								<span className="text-gray-500 ml-1">/forever</span>
							</div>
							<p className="text-gray-500">Ad-supported</p>
						</div>
						<div className="flex-grow mb-8">
							<ul className="space-y-4">
								<li className="flex items-center text-gray-600">
									<Check size={20} className="text-green-500 mr-3 flex-shrink-0" />
									<span>1 Rotating Planet/Week</span>
								</li>
								<li className="flex items-center text-gray-600">
									<Check size={20} className="text-green-500 mr-3 flex-shrink-0" />
									<span>1 Rotating Aspect/Week</span>
								</li>
								<li className="flex items-center text-gray-700">
									<X size={20} className="text-red-500 mr-3 flex-shrink-0" />
									<span>No PDF Download</span>
								</li>
								<li className="flex items-center text-gray-700">
									<X size={20} className="text-red-500 mr-3 flex-shrink-0" />
									<span>No Transits</span>
								</li>
							</ul>
						</div>
						<button
							disabled
							className={`w-full py-3 px-6 rounded-lg font-semibold border cursor-default ${user?.planTier === SUBSCRIPTION_PLANS.PREMIUM
								? "bg-gray-50 text-gray-400 border-gray-100"
								: "bg-gray-100 text-gray-600 border-gray-200"
								}`}
						>
							{user?.planTier === SUBSCRIPTION_PLANS.PREMIUM ? "Included" : "Current Plan"}
						</button>
					</div>

					<div
						className="rounded-2xl shadow-xl border-2 border-indigo-600 p-8 flex flex-col hover:shadow-2xl transition-shadow duration-300 relative overflow-hidden text-black h-full"
						style={{
							background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.94))",
						}}
					>
						<div
							className={`absolute top-0 right-0 text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wide !overflow-hidden ${hasActiveSubscription
								? "bg-green-600"
								: "bg-indigo-600"
								}`}
						>
							{hasActiveSubscription
								? "Active Plan"
								: "Professional"}
						</div>
						<div className="text-center mb-8">
							<h3 className="text-2xl font-semibold text-gray-900 mb-2">Monthly Access</h3>
							<div className="flex justify-center items-baseline mb-2">
								<span className="text-5xl font-bold text-gray-900">$49.99</span>
								<span className="text-gray-500 ml-1">/mo</span>
							</div>
							<p className="text-indigo-600 font-medium">For serious astrologers</p>
							{hasActiveSubscription && (
								<div className="mt-4 p-2 bg-indigo-100 rounded-lg">
									<p className="text-indigo-800 font-semibold">
										Your Credits: {user?.credits || 0}
									</p>
									{user?.subscriptionEndDate && (
										<p className="text-xs text-indigo-600 mt-1">
											Expires in{" "}
											{Math.max(
												0,
												Math.ceil(
													(new Date(user.subscriptionEndDate) - new Date()) /
													(1000 * 60 * 60 * 24)
												)
											)}{" "}
											days
										</p>
									)}
								</div>
							)}
						</div>
						<div className="flex-grow mb-8">
							<ul className="space-y-4">
								<li className="flex items-center text-gray-700 font-medium">
									<Check size={20} className="text-indigo-600 mr-3 flex-shrink-0" />
									<span>
										<strong>Unlimited</strong> Features
									</span>
								</li>
								<li className="flex items-center text-gray-700">
									<Check size={20} className="text-indigo-600 mr-3 flex-shrink-0" />
									<span>30 Credits / Month (1 credit = 1 Interpretation)</span>
								</li>
								<li className="flex items-center text-gray-700">
									<Check size={20} className="text-indigo-600 mr-3 flex-shrink-0" />
									<span>Reports Included for Unlocked Charts</span>
								</li>
								<li className="flex items-center text-gray-700">
									<Check size={20} className="text-indigo-600 mr-3 flex-shrink-0" />
									<span>No Ads</span>
								</li>
							</ul>
						</div>
						<button
							className="w-full py-4 px-6 rounded-lg font-bold shadow-md transition-all duration-200 transform text-white bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg hover:-translate-y-0.5"
							onClick={() => setIsUpgradeModalOpen(true)}
						>
							{hasActiveSubscription
								? "Top Up Credits"
								: "Subscribe Now"}
						</button>
					</div>
				</div>

				{hasActiveSubscription && (
					<div
						className="rounded-2xl shadow-lg border border-indigo-200 p-6 md:p-8 mb-16 max-w-4xl mx-auto text-black"
						style={{
							background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.94))",
						}}
					>
						<h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Credit Top-Up Packs</h2>
						<p className="text-center text-gray-500 mb-8 text-sm">
							One-time credit packs for active subscribers. 1 credit unlocks 1 unique chart. Credits expire when your subscription ends.
						</p>
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							{CREDIT_TOPUP_PACKS.map((pack) => (
								<button
									key={pack.planType}
									type="button"
									onClick={() => handlePurchase(pack.planType)}
									className="rounded-xl border-2 border-indigo-200 p-6 text-center hover:border-indigo-600 hover:bg-indigo-50 transition-all"
								>
									<p className="text-2xl font-bold text-gray-900">{pack.credits}</p>
									<p className="text-sm text-gray-500 mb-3">Credits</p>
									<p className="text-lg font-semibold text-indigo-600">${pack.price.toFixed(2)}</p>
								</button>
							))}
						</div>
					</div>
				)}

				<div
					className="rounded-2xl shadow-lg p-6 md:p-8 overflow-hidden text-black"
					style={{
						background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(246, 248, 255, 0.94))",
					}}
				>
					<h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Feature Comparison</h2>
					<div className="overflow-x-auto">
						<div className="min-w-[600px] pb-4">
							<div className="grid grid-cols-[2fr_1fr_1fr] p-4 bg-gray-50 rounded-lg font-semibold text-gray-900 mb-4">
								<div>Feature</div>
								<div className="text-center">Free</div>
								<div className="text-center text-indigo-600">Monthly</div>
							</div>

							<FeatureRow
								name="Chart Calculation"
								free="∞"
								monthly="30/mo"
							/>
							<FeatureRow
								name="Major Planets (9)"
								free="1/Day"
								monthly={<Check size={20} className="text-indigo-600" />}
							/>
							<FeatureRow
								name="Dwarf Planets (15)"
								free={<X size={20} className="text-gray-700" />}
								monthly={<Check size={20} className="text-indigo-600" />}
							/>
							<FeatureRow
								name="Major Aspects"
								free="1/Day"
								monthly={<Check size={20} className="text-indigo-600" />}
							/>
							<FeatureRow
								name="Minor Aspects"
								free={<X size={20} className="text-gray-700" />}
								monthly={<Check size={20} className="text-indigo-600" />}
							/>
							<FeatureRow
								name="Chart Interpretation"
								free={<X size={20} className="text-gray-700" />}
								monthly="Full"
							/>
							<FeatureRow
								name="Transits"
								free={<X size={20} className="text-gray-700" />}
								monthly="Included"
							/>
							<FeatureRow
								name="PDF Download"
								free={<X size={20} className="text-gray-700" />}
								monthly={<Check size={20} className="text-indigo-600" />}
							/>
							<FeatureRow name="Ads" free={<Check size={20} className="text-gray-600" />} monthly={<X size={20} className="text-indigo-600" />} />
						</div>
					</div>
				</div>
			</div>

			<UpgradeModal
				isOpen={isUpgradeModalOpen}
				onClose={() => setIsUpgradeModalOpen(false)}
				onUpgrade={handlePurchase}
				isPremium={user?.planTier === SUBSCRIPTION_PLANS.PREMIUM}
				hasActiveSubscription={hasActiveSubscription}
			/>
		</div>
	);
};

export default Plans;
